<template class="overflow-hidden">
  <div id="mapContainer" class="position-relative">
    <div v-if="loading" class="loading-screen" id="loadingScreen">
      <div class="spinner-border text-light" role="status">
        <span class="visually-hidden">Chargement...</span>
      </div>
    </div>

    <p v-if="this.items.length < 1" class="alert alert-danger position-top-0 text-center fs-6" id="no-results">
      Aucun résultat trouvé pour votre recherche, veuillez réessayer
    </p>
  </div>
  <div id="sidebar" ref="sidebar"></div>
</template>

<script>
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet-sidebar/src/L.Control.Sidebar.css";

import L from "leaflet";
import Icons from '@/services/icons';

import "leaflet.markercluster/dist/leaflet.markercluster.js";
import "leaflet-sidebar/src/L.Control.Sidebar";

import HttpClient from '@/services/api/api';

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

export default {
  name: 'MapView',

  data() {
    return {
      map: null,
      sidebar: null,
      clusters: null,
      center: [44.608288, 2.032871],
      initialBounds: {},
      shouldPanToInitialBounds: true,
      loading: false,
    }
  },

  props: {
    items: Array,
  },

  watch: {
    items: function (value) {
      this.shouldPanToInitialBounds = true;
      this.reloadClusterMarkers();

      const nbMarkers = this.clusters.getLayers().length;

      if (nbMarkers < 1) {
        this.map.setView(this.center);
        return;
      }

      if (nbMarkers === 1) {
        this.clusters.getLayers()[0].fire('click');
      } else {
        this.map.fitBounds(this.clusters.getBounds(), {padding: [50, 50]});
      }
    }
  },

  mounted() {
    const accessToken = 'pk.eyJ1IjoibW9iaWxvd2ViIiwiYSI6ImNsbzJvOWxycTA0MGUyaW15Mm80ZWs5dTgifQ.S47Y4PmBzzMkJvqcbQ49fA';
    const mapLayer = L.tileLayer('https://api.mapbox.com/styles/v1/mapbox/outdoors-v9/tiles/{z}/{x}/{y}?access_token=' + accessToken);

    this.map = L.map("mapContainer", {
      layers: [mapLayer],
      center: this.center,
      zoom: 14
    });

    this.clusters = L.markerClusterGroup({showCoverageOnHover: false});
    this.clusters.on('clusterclick', () => this.shouldPanToInitialBounds = false);

    L.DomEvent.on(this.map.getContainer(), 'wheel', (e) => {
      this.shouldPanToInitialBounds = false;
    });

    this.items.forEach((item) => {
      if (item.acf.location && item.acf.location.lat && item.acf.location.lng) {

        const marker = L.marker(new L.LatLng(item.acf.location.lat, item.acf.location.lng), {
          icon: this.selectMarkerIcon(item),
          ...item,
          title: item.title.rendered
        });

        marker.on('click', this.onMarkerclick);

        this.clusters.addLayer(marker);
      }
    });

    if (this.items.length > 0) {
      this.map.addLayer(this.clusters);
      this.map.fitBounds(this.clusters.getBounds(), {padding: [50, 50]});
      this.initialBounds = this.clusters.getBounds();
    }

    this.sidebar = L.control.sidebar('sidebar', {
      position: 'left',
      autoPan: false,
    });

    this.sidebar.on('hidden', () => {
      if (this.shouldPanToInitialBounds === true) {
        this.map.fitBounds(this.initialBounds, {padding: [50, 50]});
      }
    });

    this.map.addControl(this.sidebar);

    if (this.clusters.getLayers().length === 1) {
      this.clusters.getLayers()[0].fire('click');
    }
  },

  beforeUnmount() {
    if (this.map) {
      this.map.off();
      this.map.remove();
    }
  },

  methods: {
    async onMarkerclick(e) {
      if (this.sidebar.isVisible() && this.sidebar.markerId === e.target.options.id) {
        return;
      }

      this.sidebar.markerId = e.target.options.id;

      const options = e.target.options;
      const {location, notes, services, subtitle, use_tribe_address, venue} = e.target.options.acf

      const isAssociationType = options.type.toLowerCase() === 'association';

      let address = location.address;

      if (use_tribe_address) {
        this.loading = true;
        const venueObj = await HttpClient.getVenue(venue.ID);
        address = [venueObj.address, venueObj.zip, venueObj.city].filter(Boolean).join(', ');
        this.loading = false;
      }

      let lieux_de_pratique = '';
      if (options.acf.lieu_de_pratique) {
        options.acf.lieu_de_pratique.forEach((el) => {
          lieux_de_pratique += "<div>";
          lieux_de_pratique += [el.place_name, el.address].filter(Boolean).join(' - ');
          lieux_de_pratique += "</div>";
        });
      }

      let content = `
        <h2 class="pb-3 mb-0">${options.title}</h2>

        <div class="text-orange fw-bold text-uppercase my-2">${options.type}</div>

        <p class="subtitle pb-1 fw-bold">${subtitle}</p>

        <div>
            <h3 class="text-blue text-capitalize">Adresse</h3>
            <address class="fw-bold">${address}</address>
        </div>
      `;

      if (isAssociationType && options.acf.activities) {
        content += `
          <h3>Activités proposées</h3>
          <p>${options.acf.activities}</p>
        `;
      }

      if (lieux_de_pratique !== '') {
        content += '<h3>Lieux de pratique</h3>';
        content += lieux_de_pratique;
      }


      if (!isAssociationType && services && Array.isArray(services)) {
        services.forEach((service) => {
          content += `
          <div>
            <p class="my-0 py-0 fw-bold">${service.service_name}</p>
            <p class="pb-1 mb-2"><i class="fa fa-phone" aria-hidden="true"></i>&nbsp; ${service.service_phone}</p>
          </div>
        `;
        })
      }

      if (notes) {
        content += `
          <h3 class="text-blue text-capitalize">${isAssociationType ? 'Description' : 'Notes'}</h3>
          <p>${notes}</p>
        `;
      }

      if (isAssociationType) {
        content += `
        <h3>Informations complémentaires</h3>
        <p>
          <span class="d-block"><b>Siège social :</b> ${options.acf.siege_social}</span>
          <span class="d-block"><b>Adresse de contact :</b> ${options.acf.adresse_de_contact}</span>

          <span class="${options.acf.suggested_age ? 'd-block' : 'd-none'}">
            <b>Âge conseillé :</b> ${options.acf.suggested_age}
          </span>
        </p>
      `;

        content += `
        <h3>Contact</h3>
        <p>
          <span class="d-block fw-bold">${options.acf.contact.name}</span>
          <span class="d-block"><i class="fa fa-phone"></i> ${options.acf.contact.phone}</span>
          <span class="d-block"><i class="fa fa-envelope"></i> ${options.acf.contact.email}</span>

          <span class="d-block mt-2">
            <b>Site :</b> <a target="_blank" href="${options.acf.website.ur}">${options.acf.website.anchor}</a>
          </span>
        </p>
      `;

        let socials = '';
        if (options.social_network) {
          options.acf.social_network.forEach((el) => {
            socials += "<div>";
            socials += `<a target="_blank" class="p-0 btn btn-link" href="${el.url}">
            ${el.name}
          </a>`;
            socials += "</div>";
          });
        }

        if (socials !== '') {
          content += '<h3>Réseaux sociaux</h3>';
          content += socials;
        }
      }


      this.$refs.sidebar.innerHTML = `
        <div class="container">
            ${content}
        </div>
      `;

      this.sidebar.show();

      this.map.panTo([options.acf.location.lat, options.acf.location.lng], {padding: [50, 50]});
    },

    reloadClusterMarkers() {
      this.sidebar.hide();

      if (!this.clusters) {
        return;
      }

      this.map.removeLayer(this.clusters);

      this.clusters = L.markerClusterGroup({showCoverageOnHover: false});

      this.items.forEach((item) => {
        if (item.acf.location && item.acf.location.lat && item.acf.location.lng) {

          const marker = L.marker(new L.LatLng(item.acf.location.lat, item.acf.location.lng), {
            icon: this.selectMarkerIcon(item),
            ...item,
            title: item.title.rendered
          });

          marker.on('click', this.onMarkerclick);

          this.clusters.addLayer(marker);
        }
      });

      this.clusters.on('clusterclick', () => this.shouldPanToInitialBounds = false);

      this.map.addLayer(this.clusters);
      this.initialBounds = this.clusters.getBounds();
    },

    selectMarkerIcon(item) {
      switch (item.acf.type.slug) {
        case "culture":
          return Icons.associationsCulture;
        // case "":
          // return Icons.associationsEconomique;
        case "solidarite":
          return Icons.associationsSolidarite;
        // case "":
          // return Icons.associationsSport;
        // case "":
          // return Icons.equipementsCorbeille;
        case "defibrilateur":
          return Icons.equipementsDefibrilateurs;
        case "proprete-canine":
          return Icons.equipementsPropeteCanine;
        default:
          return new L.Icon.Default();
      }
    }
  },
}
</script>

<style scoped>
#mapContainer {
  width: 100%;
  height: 100%;
}

#no-results {
  z-index: 401;
}
</style>
